import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, SlidersHorizontal, LayoutGrid, List } from 'lucide-react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ProductCard } from '../components/ProductCard';
import { PRODUCTS } from '../components/products';
import { Filters, initialFilters, type FilterState } from '../components/Filters';
import { PLPTrustBanner } from '../components/PLPTrustBanner';
import { Button } from '../components/ui/Button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/Select';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../components/ui/Sheet';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext } from '../components/ui/Pagination';
type SortKey = 'featured' | 'price-asc' | 'price-desc' | 'newest';
export function Shop() {
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [sort, setSort] = useState<SortKey>('featured');
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const filtered = useMemo(() => {
    let list = PRODUCTS.filter(p => {
      if (filters.goals.length && !p.goals.some(g => filters.goals.includes(g))) return false;
      if (filters.categories.length && !filters.categories.includes(p.category)) return false;
      if (filters.purity === '99' && p.purity < 99) return false;
      if (filters.purity === '98' && p.purity < 98) return false;
      if (p.price < filters.price[0] || p.price > filters.price[1]) return false;
      if (filters.inStockOnly && !p.inStock) return false;
      return true;
    });
    switch (sort) {
      case 'price-asc':
        list = [...list].sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        list = [...list].sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        list = [...list].reverse();
        break;
    }
    return list;
  }, [filters, sort]);
  return <div className="min-h-screen w-full bg-background" data-id="element-1733">
      <AnnouncementBar data-id="element-1734" />
      <Header data-id="element-1735" />

      <main data-id="element-1736">
        {/* Page header */}
        <section className="w-full border-b border-border" data-id="element-1737">
          <div className="container mx-auto pt-10 lg:pt-14 pb-10" data-id="element-1738">
            <nav className="flex items-center gap-1.5 text-[12px] text-muted-foreground mb-6" data-id="element-1739">
              <Link to="/" className="hover:text-foreground transition-colors" data-id="element-1740">
                Home
              </Link>
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-1741" />
              <span className="text-foreground" data-id="element-1742">Shop</span>
            </nav>
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45
          }} className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4" data-id="element-1743">
              <div className="max-w-2xl" data-id="element-1744">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-1745">
                  The catalogue
                </p>
                <h1 className="text-4xl lg:text-6xl font-semibold tracking-[-0.025em] text-foreground" data-id="element-1746">
                  All peptides<span className="font-mono text-primary" data-id="element-1747">.</span>
                </h1>
                <p className="mt-4 text-[15px] text-muted-foreground max-w-lg" data-id="element-1748">
                  Every product third-party HPLC tested. Every batch traceable.
                  Shipped cold-chain from the UK.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Category tabs */}
        <section className="w-full border-b border-border bg-card" data-id="element-1749">
          <div className="container mx-auto py-3.5" data-id="element-1750">
            <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-1751">
              {(() => {
              const CATS = ['Healing', 'GLP-1', 'Growth hormone', 'Cosmetic', 'Nootropic', 'Anti-aging'] as const;
              const isAllActive = filters.categories.length === 0;
              return <>
                    <button onClick={() => setFilters(prev => ({
                  ...prev,
                  categories: []
                }))} className={`inline-flex items-center gap-2 px-3.5 h-9 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-colors ${isAllActive ? 'bg-foreground text-background' : 'text-muted-foreground bg-background border border-border hover:text-foreground hover:border-foreground/30'}`} data-id="element-1752">
                      All peptides
                      <span className={`font-mono text-[11px] [font-feature-settings:'tnum'] ${isAllActive ? 'text-background/60' : 'text-muted-foreground/70'}`} data-id="element-1753">
                        {PRODUCTS.length}
                      </span>
                    </button>
                    {CATS.map(c => {
                  const count = PRODUCTS.filter(p => p.category === c).length;
                  const active = filters.categories.length === 1 && filters.categories[0] === c;
                  return <button key={c} onClick={() => setFilters(prev => ({
                    ...prev,
                    categories: [c]
                  }))} className={`inline-flex items-center gap-2 px-3.5 h-9 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-colors ${active ? 'bg-foreground text-background' : 'text-muted-foreground bg-background border border-border hover:text-foreground hover:border-foreground/30'}`} data-id="element-1754">
                          {c}
                          <span className={`font-mono text-[11px] [font-feature-settings:'tnum'] ${active ? 'text-background/60' : 'text-muted-foreground/70'}`} data-id="element-1755">
                            {count}
                          </span>
                        </button>;
                })}
                  </>;
            })()}
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="container mx-auto py-10 lg:py-14" data-id="element-1756">
          <div className="grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-14" data-id="element-1757">
            {/* Sidebar (desktop) */}
            <div className="hidden lg:block" data-id="element-1758">
              <div className="sticky top-24" data-id="element-1759">
                <Filters filters={filters} onChange={setFilters} data-id="element-1760" />
              </div>
            </div>

            {/* Results */}
            <div data-id="element-1761">
              <PLPTrustBanner data-id="element-1762" />

              {/* Controls */}
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3" data-id="element-1763">
                <div className="flex items-center gap-3" data-id="element-1764">
                  {/* Mobile filter trigger */}
                  <Sheet data-id="element-1765">
                    <SheetTrigger asChild data-id="element-1766">
                      <Button variant="outline" size="sm" className="rounded-full lg:hidden" data-id="element-1767">
                        <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} data-id="element-1768" />
                        Filters
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-[320px] overflow-y-auto" data-id="element-1769">
                      <SheetTitle className="sr-only" data-id="element-1770">Filters</SheetTitle>
                      <div className="mt-6" data-id="element-1771">
                        <Filters filters={filters} onChange={setFilters} data-id="element-1772" />
                      </div>
                    </SheetContent>
                  </Sheet>

                  <span className="font-mono text-[13px] text-muted-foreground" data-id="element-1773">
                    {filtered.length}{' '}
                    {filtered.length === 1 ? 'peptide' : 'peptides'}
                  </span>
                </div>

                <div className="flex items-center gap-2" data-id="element-1774">
                  <div className="hidden sm:flex items-center rounded-full border border-border p-0.5 bg-card" data-id="element-1775">
                    <button onClick={() => setLayout('grid')} aria-label="Grid view" className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${layout === 'grid' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-1776">
                      <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.75} data-id="element-1777" />
                    </button>
                    <button onClick={() => setLayout('list')} aria-label="List view" className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${layout === 'list' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-1778">
                      <List className="h-3.5 w-3.5" strokeWidth={1.75} data-id="element-1779" />
                    </button>
                  </div>

                  <Select value={sort} onValueChange={v => setSort(v as SortKey)} data-id="element-1780">
                    <SelectTrigger size="sm" className="rounded-full min-w-[170px]" data-id="element-1781">
                      <SelectValue data-id="element-1782" />
                    </SelectTrigger>
                    <SelectContent data-id="element-1783">
                      <SelectItem value="featured" data-id="element-1784">Featured</SelectItem>
                      <SelectItem value="price-asc" data-id="element-1785">
                        Price: low to high
                      </SelectItem>
                      <SelectItem value="price-desc" data-id="element-1786">
                        Price: high to low
                      </SelectItem>
                      <SelectItem value="newest" data-id="element-1787">Newest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Grid */}
              {filtered.length === 0 ? <div className="mt-10 py-20 text-center border border-dashed border-border rounded-2xl" data-id="element-1788">
                  <p className="text-muted-foreground text-sm" data-id="element-1789">
                    No peptides match these filters.
                  </p>
                  <Button variant="outline" size="sm" className="mt-4 rounded-full" onClick={() => setFilters(initialFilters)} data-id="element-1790">
                    Reset filters
                  </Button>
                </div> : <motion.div layout className={`mt-8 grid gap-5 ${layout === 'grid' ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`} data-id="element-1791">
                  {filtered.map(p => <motion.div key={p.id} layout initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.35
              }} data-id="element-1792">
                      <ProductCard product={p} detailed data-id="element-1793" />
                    </motion.div>)}
                </motion.div>}

              {/* Pagination */}
              {filtered.length > 0 && <div className="mt-14" data-id="element-1794">
                  <Pagination data-id="element-1795">
                    <PaginationContent data-id="element-1796">
                      <PaginationItem data-id="element-1797">
                        <PaginationPrevious href="#" data-id="element-1798" />
                      </PaginationItem>
                      <PaginationItem data-id="element-1799">
                        <PaginationLink href="#" isActive data-id="element-1800">
                          1
                        </PaginationLink>
                      </PaginationItem>
                      <PaginationItem data-id="element-1801">
                        <PaginationLink href="#" data-id="element-1802">2</PaginationLink>
                      </PaginationItem>
                      <PaginationItem data-id="element-1803">
                        <PaginationLink href="#" data-id="element-1804">3</PaginationLink>
                      </PaginationItem>
                      <PaginationItem data-id="element-1805">
                        <PaginationNext href="#" data-id="element-1806" />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1807" />
    </div>;
}